import type { BridgeChartStudyCode } from "../types/data";
import type { BridgeChartPaneCode, BridgeChartPaneOptions } from "../types/public";

export interface ChartPaneLayout {
  code: BridgeChartPaneCode;
  top: number;
  height: number;
}

export interface ChartLayout {
  width: number;
  height: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  volumeHeight: number;
  plotWidth: number;
  plotHeight: number;
  volumeTop: number;
  contentBottom: number;
  panes: ChartPaneLayout[];
}

interface NormalizedPaneOptions {
  gap: number;
  minimumPriceHeight: number;
  heights: Partial<Record<BridgeChartStudyCode, number>>;
}

const PANE_CODES = new Set<BridgeChartStudyCode>(["RSI_14", "MACD_12_26_9", "ATR_14", "VOLUME"]);
const MINIMUM_STUDY_HEIGHT = 28;
const MAXIMUM_STUDY_HEIGHT = 240;

export function buildLayout(
  width: number,
  height: number,
  paneCodes: readonly BridgeChartStudyCode[] = ["VOLUME"],
  paneOptions: BridgeChartPaneOptions = {}
): ChartLayout {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    throw new RangeError("BridgeChart layout dimensions must be finite positive values.");
  }
  validatePaneCodes(paneCodes);
  const options = normalizePaneOptions(paneOptions);
  const layout = {
    width,
    height,
    left: 14,
    right: 64,
    top: 16,
    bottom: 28,
    volumeHeight: 0,
    plotWidth: 0,
    plotHeight: 0,
    volumeTop: 0,
    contentBottom: 0,
    panes: [] as ChartPaneLayout[]
  };
  layout.plotWidth = width - layout.left - layout.right;
  const contentHeight = Math.max(1, height - layout.top - layout.bottom);
  const gapTotal = paneCodes.length * options.gap;
  const minimumRequiredHeight = options.minimumPriceHeight + gapTotal + paneCodes.length * MINIMUM_STUDY_HEIGHT;
  if (minimumRequiredHeight > contentHeight) {
    throw new RangeError("BridgeChart pane minimum heights do not fit the available canvas height.");
  }
  const auxiliaryBudget = Math.max(
    paneCodes.length * MINIMUM_STUDY_HEIGHT,
    contentHeight - gapTotal - options.minimumPriceHeight
  );
  const responsiveHeight = Math.round(clamp(height * 0.18, 56, 112));
  const desiredHeights = paneCodes.map(code => options.heights[code] ?? responsiveHeight);
  const paneHeights = fitPaneHeights(desiredHeights, auxiliaryBudget);
  const usedAuxiliaryHeight = paneHeights.reduce((sum, value) => sum + value, 0);
  layout.plotHeight = Math.max(1, contentHeight - gapTotal - usedAuxiliaryHeight);
  layout.panes.push({ code: "PRICE", top: layout.top, height: layout.plotHeight });

  let nextTop = layout.top + layout.plotHeight;
  paneCodes.forEach((code, index) => {
    nextTop += options.gap;
    layout.panes.push({ code, top: nextTop, height: paneHeights[index] });
    nextTop += paneHeights[index];
  });
  layout.volumeTop = layout.panes[1]?.top ?? nextTop;
  layout.volumeHeight = Math.max(0, nextTop - layout.volumeTop);
  layout.contentBottom = nextTop;
  return layout;
}

function normalizePaneOptions(value: BridgeChartPaneOptions): NormalizedPaneOptions {
  const gap = value.gap ?? 8;
  const minimumPriceHeight = value.minimumPriceHeight ?? 96;
  if (!Number.isInteger(gap) || gap < 0 || gap > 24) {
    throw new RangeError("BridgeChart pane gap must be an integer from 0 through 24.");
  }
  if (!Number.isInteger(minimumPriceHeight) || minimumPriceHeight < 80 || minimumPriceHeight > 400) {
    throw new RangeError("BridgeChart minimum price pane height must be an integer from 80 through 400.");
  }
  const heights: Partial<Record<BridgeChartStudyCode, number>> = {};
  for (const [rawCode, rawHeight] of Object.entries(value.heights ?? {})) {
    const code = rawCode as BridgeChartStudyCode;
    if (!PANE_CODES.has(code)) {
      throw new TypeError(`BridgeChart pane height code '${rawCode}' is not an independently scaled pane.`);
    }
    if (!Number.isInteger(rawHeight) || rawHeight < MINIMUM_STUDY_HEIGHT || rawHeight > MAXIMUM_STUDY_HEIGHT) {
      throw new RangeError(`BridgeChart pane height for '${rawCode}' must be an integer from ${MINIMUM_STUDY_HEIGHT} through ${MAXIMUM_STUDY_HEIGHT}.`);
    }
    heights[code] = rawHeight;
  }
  return { gap, minimumPriceHeight, heights };
}

function validatePaneCodes(codes: readonly BridgeChartStudyCode[]): void {
  const unique = new Set<BridgeChartStudyCode>();
  codes.forEach(code => {
    if (!PANE_CODES.has(code)) {
      throw new TypeError(`BridgeChart pane code '${code}' is not an independently scaled pane.`);
    }
    if (unique.has(code)) {
      throw new TypeError(`BridgeChart pane code '${code}' is duplicated.`);
    }
    unique.add(code);
  });
}

function fitPaneHeights(desired: readonly number[], budget: number): number[] {
  if (desired.length === 0) return [];
  const desiredTotal = desired.reduce((sum, value) => sum + value, 0);
  if (desiredTotal <= budget) return [...desired];
  const floorTotal = desired.length * MINIMUM_STUDY_HEIGHT;
  const distributable = Math.max(0, budget - floorTotal);
  const desiredExtra = Math.max(1, desiredTotal - floorTotal);
  const fitted = desired.map(value => MINIMUM_STUDY_HEIGHT + Math.floor((value - MINIMUM_STUDY_HEIGHT) / desiredExtra * distributable));
  let remainder = Math.max(0, budget - fitted.reduce((sum, value) => sum + value, 0));
  for (let index = 0; index < fitted.length && remainder > 0; index++, remainder--) fitted[index]++;
  return fitted;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
